import { getFirebaseAuth } from './firebase-init';

// Defaults to same-origin /api since the app is always served alongside its own
// backend (nginx proxies /api/ to the Node server).
function getApiUrl() {
  return localStorage.getItem('cc_api_url') || `${window.location.origin}/api`;
}

async function request(path, options = {}) {
  const url = `${getApiUrl()}${path}`;
  const currentUser = getFirebaseAuth().currentUser;
  const token = currentUser ? await currentUser.getIdToken() : null;
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    const e = new Error(err.error || 'Request failed');
    e.status = res.status;
    e.data = err;   // lets callers react to structured errors, e.g. { code: 'watch_more', watched_s, needed_s }
    throw e;
  }
  // CSV responses
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('text/csv')) return res.blob();
  return res.json();
}

export const api = {
  setApiUrl(apiUrl) {
    localStorage.setItem('cc_api_url', apiUrl);
  },
  getApiUrl,

  // Users
  getMe: () => request('/users/me'),
  getUsers: () => request('/users'),
  createUser: (data) => request('/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id, data) => request(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteUser: (id) => request(`/users/${id}`, { method: 'DELETE' }),

  // Classes
  getClasses: () => request('/classes'),
  createClass: (data) => request('/classes', { method: 'POST', body: JSON.stringify(data) }),
  updateClass: (id, data) => request(`/classes/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteClass: (id) => request(`/classes/${id}`, { method: 'DELETE' }),
  getClassStudents: (id) => request(`/classes/${id}/students`),
  assignStudent: (classId, studentId) => request(`/classes/${classId}/assign`, { method: 'POST', body: JSON.stringify({ student_id: studentId }) }),
  unassignStudent: (classId, studentId) => request(`/classes/${classId}/assign/${studentId}`, { method: 'DELETE' }),

  // Videos (admin): request, review, mark ready
  getVideos: () => request('/videos'),
  getWorkerStatus: () => request('/videos/worker-status'),
  requestVideo: (data) => request('/videos', { method: 'POST', body: JSON.stringify(data) }),
  getVideo: (id) => request(`/videos/${id}`),
  updateVideoQuestion: (videoId, qid, data) => request(`/videos/${videoId}/questions/${qid}`, { method: 'PATCH', body: JSON.stringify(data) }),
  markVideoReady: (id) => request(`/videos/${id}/ready`, { method: 'POST' }),
  retryVideo: (id) => request(`/videos/${id}/retry`, { method: 'POST' }),
  deleteVideo: (id) => request(`/videos/${id}`, { method: 'DELETE' }),

  // Video assignments. Students: list, start a quiz, submit it. Admin: assign, results, remove.
  getVideoToday: (userId) => request(`/video-assignments/today${userId ? `?userId=${userId}` : ''}`),
  startVideoAttempt: (assignmentId, partIdx) => request(`/video-assignments/${assignmentId}/parts/${partIdx}/attempts`, { method: 'POST' }),
  submitVideoAttempt: (attemptId, answers) => request(`/video-assignments/attempts/${attemptId}/submit`, { method: 'POST', body: JSON.stringify({ answers }) }),
  assignVideo: (data) => request('/video-assignments', { method: 'POST', body: JSON.stringify(data) }),
  getVideoResults: (params = {}) => request(`/video-assignments/results?${new URLSearchParams(params)}`),
  reportVideoWatch: (assignmentId, partIdx, seconds) => request(`/video-assignments/${assignmentId}/parts/${partIdx}/watch`, { method: 'POST', body: JSON.stringify({ seconds }) }),
  waiveAllVideoWatch: (assignmentId) => request(`/video-assignments/${assignmentId}/waive-watch`, { method: 'POST' }),
  waiveVideoWatch: (assignmentId, partIdx) => request(`/video-assignments/${assignmentId}/parts/${partIdx}/waive-watch`, { method: 'POST' }),
  getVideoStats: (videoId) => request(`/videos/${videoId}/stats`),
  deleteVideoAssignment: (id) => request(`/video-assignments/${id}`, { method: 'DELETE' }),

  // Completions
  getToday: (userId) => request(`/completions/today${userId ? `?userId=${userId}` : ''}`),
  completeClass: (data) => request('/completions', { method: 'POST', body: JSON.stringify(data) }),
  getCompletions: (userId, from, to) => {
    const params = new URLSearchParams();
    if (userId) params.set('userId', userId);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return request(`/completions?${params}`);
  },
  getCalendar: (userId, year, month) => {
    const params = new URLSearchParams({ year, month });
    if (userId) params.set('userId', userId);
    return request(`/completions/calendar?${params}`);
  },
  getDay: (userId, date) => {
    const params = new URLSearchParams({ date });
    if (userId) params.set('userId', userId);
    return request(`/completions/day?${params}`);
  },
  exportCSV: async (userId, userName) => {
    const params = userId ? `?userId=${userId}` : '';
    const blob = await request(`/completions/export${params}`, {
      headers: { 'Content-Type': '' },
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${userName || 'student'}-history.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },
};
